import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Item } from '../components/conversation/Item';
import { PortalSpinner } from '../components/PortalSpinner';
import { useConversationsQuery } from '../queries/conversations';
import { PORTAL_ROUTES } from '../constants';

export const Conversations = () => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useConversationsQuery(debouncedSearch);
  const conversations = data?.pages.flatMap((p) => p.items) ?? [];

  const [sentinelRef, { rootRef }] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
  });

  return (
    <>
      <div className="flex items-center px-4 pt-5 pb-3 flex-shrink-0">
        <h2 className="text-lg font-bold text-secondary-900">Inbox</h2>
      </div>
      <div className="px-4 pb-3 flex-shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-teamcare-blue focus:bg-white transition-colors placeholder:text-secondary-400"
          />
        </div>
      </div>
      <div ref={rootRef} className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex justify-center pt-10">
            <PortalSpinner />
          </div>
        )}
        {isError && (
          <p className="text-sm text-center text-secondary-400 pt-10">
            Failed to load conversations.
          </p>
        )}
        {!isLoading && !isError && conversations.length === 0 && (
          <p className="text-sm text-center text-secondary-400 pt-10">
            No conversations yet.
          </p>
        )}
        {conversations.map((conv) => (
          <Item
            key={conv.id}
            conv={conv}
            isSelected={id === conv.attributes.encoded_id}
            to={`${PORTAL_ROUTES.CONVERSATIONS}/${conv.attributes.encoded_id}`}
          />
        ))}
        {(hasNextPage || isFetchingNextPage) && (
          <div ref={sentinelRef} className="flex justify-center py-1">
            <PortalSpinner />
          </div>
        )}
      </div>
    </>
  );
};
